import { FC, MouseEvent, useMemo } from "react"
import classNames from "classnames"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import { ButtonVariant, ColorVariant } from "@/types/index"
import { isUndefined } from "@/utils/lodash"
import "./index.scss"

export enum ButtonIconPosition {
   LEFT = "left",
   RIGHT = "right",
}

interface ButtonProps {
   children?: string
   className?: string
   variant?: ButtonVariant
   color?: ColorVariant
   iconName?: IconProps["name"]
   iconSize?: IconProps["size"]
   iconPosition?: ButtonIconPosition
   fontSize?: string
   disabled?: boolean
   testId?: string
   outlined?: boolean
   onClick: (e: MouseEvent) => void
}

const Button: FC<ButtonProps> = (props) => {
   const {
      children,
      className,
      variant = ButtonVariant.DEFAULT,
      color = ColorVariant.HONOLULU_BLUE,
      iconName,
      iconSize,
      iconPosition,
      testId,
      disabled = false,
      outlined = false,
      ...rest
   } = props

   const buttonColor = useMemo(() => {
      const colors: Partial<Record<ColorVariant, string>> = {
         [ColorVariant.HONOLULU_BLUE]: "--honolulu-blue",
         [ColorVariant.CERISE]: "--cerise",
      }

      return colors[color]
   }, [color])

   const iconColor = useMemo(() => {
      if (!outlined) {
         return IconColor.WHITE
      }
      const colors: Partial<Record<ColorVariant, IconColor>> = {
         [ColorVariant.HONOLULU_BLUE]: IconColor.HONOLULU_BLUE,
         [ColorVariant.CERISE]: IconColor.CERISE,
      }

      return colors[color]
   }, [color, outlined])

   const getIcon = useMemo(() => {
      if (isUndefined(iconName)) {
         return null
      }
      return (
         <Icon
            size={iconSize || "16"}
            name={iconName}
            color={iconColor}
         />
      )
   }, [iconSize, iconName, iconColor])

   return (
      <button
         className={classNames("button", `button${buttonColor}`, {
            [`${className}`]: className,
            "button--lg": variant === ButtonVariant.LARGE,
            "button--sm": variant === ButtonVariant.SMALL,
            "button--with-icon": iconName,
            "button--outlined": outlined,
         })}
         disabled={disabled}
         data-testid={testId}
         {...rest}
      >
         {iconName && iconPosition === ButtonIconPosition.LEFT && getIcon}
         {children}
         {iconName && iconPosition === ButtonIconPosition.RIGHT && getIcon}
      </button>
   )
}

export default Button
